// src/components/BenchmarkSuggestionBadge.tsx
//
// A small, self-contained pill that surfaces the auto-suggested benchmark next
// to the comparison dropdown. Shows a skeleton while loading, an "Apply"
// button that hoists the suggested id up to the parent, and a dismiss link that
// hides it for the current session. Renders nothing when there is no suggestion
// or an error occurred (the picker must never break on a failed suggestion).

import { useEffect, useState } from 'react';

import type { BenchmarkSuggestionResponse } from '../features/portfolio-optimization-tools/multi-asset-portfolio-optimizer/api';

interface Props {
    suggestion: BenchmarkSuggestionResponse | null;
    isLoading: boolean;
    error: string | null;
    /** Called with the suggested catalogue id when the user clicks "Apply". */
    onApply: (suggestedId: string) => void;
    /** The id currently selected in the dropdown -- drives the "Applied" state. */
    currentBenchmarkId?: string;
}

const SparkleIcon = ({ className }: { className?: string }) => (
    <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
    </svg>
);

export const BenchmarkSuggestionBadge = ({
    suggestion,
    isLoading,
    error,
    onApply,
    currentBenchmarkId,
}: Props) => {
    const [dismissed, setDismissed] = useState(false);
    const [showReason, setShowReason] = useState(false);

    // A fresh suggestion (different id) should un-dismiss the badge.
    useEffect(() => {
        setDismissed(false);
    }, [suggestion?.suggested_id]);

    if (error || dismissed) return null;

    if (isLoading) {
        return (
            <div className="h-7 w-40 rounded-full bg-slate-200 dark:bg-slate-800 animate-pulse" />
        );
    }

    if (!suggestion) return null;

    const isApplied = currentBenchmarkId === suggestion.suggested_id;

    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
                <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-indigo-500/30 bg-indigo-50 dark:bg-indigo-500/10 px-2.5 py-1 text-[11px] font-bold text-indigo-600 dark:text-indigo-300"
                    title={suggestion.reason}
                >
                    <SparkleIcon className="w-3 h-3" />
                    Suggested: {suggestion.suggested_name}
                </span>

                {isApplied ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-500">
                        Applied
                    </span>
                ) : (
                    <button
                        type="button"
                        onClick={() => onApply(suggestion.suggested_id)}
                        className="text-[10px] font-bold uppercase tracking-wider text-indigo-500 hover:text-indigo-400 transition-colors"
                    >
                        Apply
                    </button>
                )}

                <button
                    type="button"
                    onClick={() => setShowReason((v) => !v)}
                    className="text-[10px] font-semibold text-slate-400 hover:text-slate-500 transition-colors"
                >
                    {showReason ? 'Hide' : 'Why?'}
                </button>

                <button
                    type="button"
                    onClick={() => setDismissed(true)}
                    className="text-[10px] font-semibold text-slate-400 hover:text-red-400 transition-colors"
                    aria-label="Dismiss suggestion"
                >
                    Dismiss
                </button>
            </div>

            {showReason && (
                <p className="text-[10px] text-slate-500 max-w-xs leading-snug">
                    {suggestion.reason}
                </p>
            )}
        </div>
    );
};

export default BenchmarkSuggestionBadge;
